import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import theme from "../utils/theme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export const filterFieldTooltipProps = {
  placement: "bottom-end" as const,
  arrow: true,
  enterDelay: 800,
  slotProps: {
    popper: {
      modifiers: [
        {
          name: "offset",
          options: { offset: [0, -2] },
        },
      ],
    },
    tooltip: {
      sx: { maxWidth: 200 },
    },
  },
};

export interface DropdownOption {
  label: string;
  value: string;
}

export interface DropdownProps {
  label: string;
  placeholder: string;
  options: DropdownOption[];
  loading?: boolean;
  disabled?: boolean;
  error?: string;
}

const selectedValueTextSx = {
  display: "inline-block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "0.875rem",
  lineHeight: 1.4,
  pl: "8px",
} as const;

const SelectedValueTooltip: React.FC<{
  title: string;
  children: React.ReactElement;
}> = ({ title, children }) => (
  <Tooltip
    title={title}
    disableHoverListener={!title}
    {...filterFieldTooltipProps}
  >
    {children}
  </Tooltip>
);

const SingleSelectDropdown: React.FC<
  DropdownProps & {
    onSelect: (selectedValue: string) => void;
    selectedValue: string | null | undefined;
    labelMarginBottom?: string;
  }
> = ({
  label,
  placeholder,
  options,
  onSelect,
  selectedValue,
  loading,
  disabled,
  error,
  labelMarginBottom = "8px",
}) => {
  const theme = useTheme();
  const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
    const selectedValue = value ? value.value : null;
    onSelect(selectedValue);
  };

  const selectedOption =
    options.find((option) => option.value === selectedValue) ?? null;

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontSize: "12px",
          fontWeight: 400,
          color: theme.palette.text.primary,
          mb: labelMarginBottom ?? "8px",
        }}
      >
        {label}
      </Typography>
      <div
        style={{ position: "relative", minWidth: "150px", paddingTop: "4px" }}
      >
        <Autocomplete
          slots={{ popper: CustomPopper }}
          options={options}
          getOptionKey={(option) => option.label + option.value}
          value={selectedOption}
          ListboxProps={{
            style: {
              maxHeight: "200px",
              overflowY: "auto",
            },
          }}
          sx={{
            "&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-endAdornment":
              {
                height: "100%",
                paddingTop: "8px",
                borderColor: "red",
                fontSize: "0.875rem",
              },
            ".MuiOutlinedInput-notchedOutline ": {
              borderColor: "rgba(218, 218, 218, 1)",
            },
            "& .MuiOutlinedInput-root": {
              minHeight: 40,
            },
            "& .MuiInputBase-input": {
              paddingLeft: "8px",
              paddingY: "8px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              fontSize: "0.875rem",
            },
          }}
          onChange={handleChange}
          autoComplete={true}
          disabled={disabled}
          popupIcon={<ExpandMoreIcon sx={{ color: "#343A40" }} />}
          renderValue={(value) =>
            value ? (
              <SelectedValueTooltip title={value.label}>
                <Box component="span" sx={selectedValueTextSx}>
                  {value.label}
                </Box>
              </SelectedValueTooltip>
            ) : null
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={selectedOption ? "" : placeholder}
              size="small"
              error={Boolean(error)}
              helperText={error}
              sx={{
                "& .MuiInputBase-root": {
                  cursor: "pointer",
                },
                "& .MuiInputBase-input": {
                  "&:hover": {
                    cursor: "pointer",
                  },
                },
              }}
            />
          )}
        />
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 999,
            }}
          >
            <CircularProgress size={24} />
          </div>
        )}
      </div>
    </>
  );
};

const MultiSelectDropdown: React.FC<
  DropdownProps & {
    onSelect: (selectedValues: string[], selectedLabels: string[]) => void;
    selectedValues: string[];
    labelMarginBottom?: string;
    marginBottom?: string;
  }
> = ({
  label,
  placeholder,
  options,
  onSelect,
  selectedValues,
  loading,
  labelMarginBottom = "5px",
  marginBottom = "14px",
}) => {
  const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
    const selectedValues = value.map((item: any) => item.value);
    const selectedLabels = value.map((item: any) => item.label);
    onSelect(selectedValues, selectedLabels);
  };

  const selectedOptions = (() => {
    const filtered = options.filter((option) =>
      selectedValues.includes(option.value),
    );
    if (options.length === 0 && selectedValues.length > 0) {
      return selectedValues.map((value) => {
        if (value === "___EMPTY___") {
          return { label: "All", value: value };
        }
        return { label: value, value: value };
      });
    }

    return filtered;
  })();

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontSize: "12px",
          fontWeight: 400,
          color: theme.palette.text.primary,
          mb: labelMarginBottom,
        }}
      >
        {label}
      </Typography>
      <div style={{ position: "relative" }}>
        <Autocomplete
          sx={{
            mb: marginBottom,
            "& .MuiOutlinedInput-root": {
              minHeight: 40,
            },
            ...(selectedValues.length > 0 && {
              "&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-endAdornment":
                {
                  height: "100%",
                  paddingTop: "8px",
                  borderColor: "red",
                },
              ".MuiOutlinedInput-notchedOutline ": {
                borderColor: "rgba(218, 218, 218, 1)",
              },
              "& .MuiInputBase-input": {
                paddingLeft: "8px",
                paddingY: "8px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                fontSize: "0.875rem",
              },
            }),
          }}
          multiple
          options={options}
          disableCloseOnSelect
          popupIcon={<ExpandMoreIcon sx={{ color: "#343A40" }} />}
          getOptionKey={(option) => option.label + option.value}
          value={selectedOptions}
          onChange={handleChange}
          ListboxProps={{
            style: {
              maxHeight: "150px",
            },
          }}
          renderValue={(tagValue, getItemProps) =>
            tagValue.map((option, index) => {
              const { key, ...itemProps } = getItemProps({ index });
              return (
                <SelectedValueTooltip key={key} title={option.label}>
                  <Chip
                    size="small"
                    label={option.label}
                    {...itemProps}
                    sx={{
                      maxWidth: "100%",
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </SelectedValueTooltip>
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              variant="outlined"
              placeholder={placeholder}
              sx={{
                "& .MuiInputBase-root": {
                  maxHeight: "100px",
                  position: "static",
                  overflowY: "auto",
                  cursor: "pointer",
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  "&:hover": {
                    cursor: "pointer",
                  },
                },
              }}
            />
          )}
        />

        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 999,
            }}
          >
            <CircularProgress size={24} />
          </div>
        )}
      </div>
    </>
  );
};

const CustomPopper = (props: any) => (
  <Popper
    {...props}
    style={{ zIndex: 1400 }}
    placement="bottom-start"
    modifiers={[
      {
        name: "setWidth",
        enabled: true,
        phase: "beforeWrite",
        requires: ["computeStyles"],
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
      },
    ]}
  />
);

export { SingleSelectDropdown, MultiSelectDropdown, SelectedValueTooltip };
